import DropdownToggle from './DropdownToggle';

/**
 * Learning-mode picker. Opening is the shared `DropdownToggle`; the pick is ours.
 *
 * Persistence is deliberately absent: a pick repaints the trigger and announces itself with a
 * `changeLearningMode` event on `document`. The LMS listens and saves (`lms/LearningModePersist`),
 * the website listens and blanks the outline columns (`website/CourseOutlineModeFilter`). That is
 * what lets the same picker ship in a bundle that has none of the LMS page globals.
 *
 * ⛔ The panel is CLONED from a `<template>` on every open, never left in the DOM.
 * `CustomCoursesPage.closeSelectOutsideClick()` removes every `.select-menu` on any outside click,
 * serving the live `select-menu-module` / `select-menu-topic` submenus, and its bundle loads on the
 * three legacy hosts — so a persistent panel was destroyed there for good after one pick.
 * `CreateNewCourseModule`'s same-named method only removes `.select-menu-module` and never hit us.
 *
 * Its own `DropdownToggle` instance, not the document-wide one in `scripts-lms`: a pick has to close
 * the menu, and an inside click never does.
 */
export default class LearningModeSelect {
    constructor() {
        this.classList = {
            root: 'js-learning-mode',
            trigger: 'js-learning-mode-trigger',
            template: 'js-learning-mode-panel',
            option: 'js-learning-mode-option',
            panel: 'select-menu',
            active: 'is-active',
        };

        this.roots = [...document.querySelectorAll(`.${this.classList.root}`)]
            .filter((root) => root.querySelector(`.${this.classList.template}`));

        if (!this.roots.length) {
            return;
        }

        this.$onTriggerClick = this.onTriggerClick.bind(this);
        this.$onRootClick = this.onRootClick.bind(this);

        // Before the toggle: the panel has to exist by the time it sets the open state.
        this.roots.forEach((root) => {
            root.querySelector(`.${this.classList.trigger}`)
                .addEventListener('click', this.$onTriggerClick);
            // Delegated, so a freshly cloned panel needs no rebinding.
            root.addEventListener('click', this.$onRootClick);
        });

        this.dropdown = new DropdownToggle({
            rootClass: this.classList.root,
            triggerClass: this.classList.trigger,
        });
    }

    /**
     * @param {MouseEvent} event
     */
    onTriggerClick(event) {
        this.ensurePanel(event.currentTarget.closest(`.${this.classList.root}`));
    }

    /**
     * @param {HTMLElement} root
     */
    ensurePanel(root) {
        const anchor = root.querySelector(`.${this.classList.template}`).parentElement;

        if (anchor.querySelector(`.${this.classList.panel}`)) {
            return;
        }

        const template = root.querySelector(`.${this.classList.template}`);

        anchor.appendChild(template.content.cloneNode(true));

        // The template's own active row is whatever the page rendered with, so re-point it at the
        // trigger, which `applyPick` keeps current.
        const mode = root.querySelector(`.${this.classList.trigger}`).getAttribute('value');

        root.querySelectorAll(`.${this.classList.option}`).forEach((option) => {
            option.classList.toggle(this.classList.active, option.dataset.value === mode);
        });
    }

    /**
     * @param {MouseEvent} event
     */
    onRootClick(event) {
        const option = event.target.closest(`.${this.classList.option}`);

        if (!option) {
            return;
        }

        const root = option.closest(`.${this.classList.root}`);

        this.applyPick(root, option);

        // The panel is `display: none` when closed, so a keyboard user would lose focus without this.
        this.dropdown.close(true);

        document.dispatchEvent(new CustomEvent('changeLearningMode', {
            detail: { learningModeID: option.dataset.value },
        }));
    }

    /**
     * @param {HTMLElement} root
     * @param {HTMLElement} option
     */
    applyPick(root, option) {
        const trigger = root.querySelector(`.${this.classList.trigger}`);
        const mode = option.dataset.value;

        trigger.setAttribute('value', mode);
        trigger.querySelector('span').textContent = option.dataset.modeName;

        // Built from the id rather than copied off the row: the row's `classList` is styling.
        trigger.querySelector('i').className = `icon icon-module_${mode}`;

        root.querySelectorAll(`.${this.classList.option}.${this.classList.active}`)
            .forEach((row) => row.classList.remove(this.classList.active));
        option.classList.add(this.classList.active);
    }

    destroy() {
        this.roots.forEach((root) => {
            root.querySelector(`.${this.classList.trigger}`)
                .removeEventListener('click', this.$onTriggerClick);
            root.removeEventListener('click', this.$onRootClick);
        });

        if (this.dropdown) {
            this.dropdown.destroy();
        }
    }
}
